// IntroScreenManager.jsx - Updated to match your API structure
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  IconButton,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Dialog,
  DialogContent,
  Chip,
  Stack,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import {
  Upload,
  Delete,
  DragIndicator,
  Add,
  Save,
  Smartphone,
  Close,
  Image as ImageIcon,
  VideoLibrary,
  Gif,
} from "@mui/icons-material";
import Slide from "@mui/material/Slide";
import { canShowPermission } from "../Permissions/permissionObject";
import { controlAllFeatureOptions } from "../../../configs/constants";

const BASE_API = `${process.env.REACT_APP_API_BASE_URL}/api/v1/intro-screen`;
const SHOW_INTRO_SCREEN_KEY = controlAllFeatureOptions.Show_Intro_Screen;

const IntroScreenManager = () => {
  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (e) {
        console.error("Failed to parse userPermissions", e);
      }
    }
  }, []);

  const canCreate = canShowPermission(permissions, "introScreen", "create");
  const canEdit = canShowPermission(permissions, "introScreen", "edit");
  const canDelete = canShowPermission(permissions, "introScreen", "delete");

  const [screens, setScreens] = useState([]);
  const [showIntroScreen, setShowIntroScreen] = useState(false);
  const [featureToggle, setFeatureToggle] = useState({});
  const [previewMode, setPreviewMode] = useState(true);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [draggedItem, setDraggedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [togglingScreenId, setTogglingScreenId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchScreens();
    fetchShowIntroScreenSetting();
  }, []);

  const fetchShowIntroScreenSetting = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/setting/getAppConfig`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const toggle = response.data?.data?.featureToggle || {};
      setFeatureToggle(toggle);
      setShowIntroScreen(toggle[SHOW_INTRO_SCREEN_KEY] === true);
    } catch (error) {
      console.error("Error fetching intro screen setting:", error);
    }
  };

  const saveShowIntroScreenSetting = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/setting/updateKeyValue`,
        {
          key: "featureToggle",
          value: {
            ...featureToggle,
            [SHOW_INTRO_SCREEN_KEY]: showIntroScreen,
          },
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setFeatureToggle((prev) => ({
        ...prev,
        [SHOW_INTRO_SCREEN_KEY]: showIntroScreen,
      }));
      showSnackbar("Show intro screen setting saved", "success");
    } catch (error) {
      console.error(
        "Error saving intro screen setting:",
        error.response?.data || error.message
      );
      showSnackbar(
        error.response?.data?.message || "Failed to save intro screen setting",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchScreens = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const response = await axios.get(`${BASE_API}/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("FETCH SCREENS RESPONSE:", response.data);

      // Your backend response structure
      if (response.data?.data && Array.isArray(response.data.data)) {
        setScreens(
          response.data.data.map((item) => ({
            id: item._id, // normalize id
            ...item,
            files: [], // frontend-only
            filePreviews: [],
            isNew: false,
          }))
        );
      } else {
        setScreens([]);
      }
    } catch (error) {
      console.error("Error fetching screens:", error);
      setScreens([]);
    } finally {
      setLoading(false);
    }
  };

  const addNewScreen = () => {
    const newScreen = {
      id: Date.now(),
      type: "image",
      files: [],
      title: "",
      description: "",
      order: screens.length,
      isActive: true,
      isNew: true,
    };
    setScreens([...screens, newScreen]);
  };

  const handleFileSelect = (screenId, files) => {
    const fileArray = Array.from(files);
    setScreens(
      screens.map((s) => {
        if (s.id === screenId) {
          return {
            ...s,
            files: fileArray,
            filePreviews: fileArray.map((file) => URL.createObjectURL(file)),
          };
        }
        return s;
      })
    );
  };

  const removeFile = (screenId, fileIndex) => {
    setScreens(
      screens.map((s) => {
        if (s.id === screenId) {
          const newFiles = s.files.filter((_, idx) => idx !== fileIndex);
          const newPreviews = s.filePreviews?.filter(
            (_, idx) => idx !== fileIndex
          );
          return {
            ...s,
            files: newFiles,
            filePreviews: newPreviews,
          };
        }
        return s;
      })
    );
  };

  const updateScreen = (id, field, value) => {
    setScreens(
      screens.map((s) => (s.id === id ? { ...s, [field]: value } : s))
    );
  };

  const updateScreenStatus = async (screen, isActive) => {
    setScreens((prev) =>
      prev.map((s) => (s.id === screen.id ? { ...s, isActive } : s))
    );

    if (screen.isNew) return;

    try {
      setTogglingScreenId(screen.id);
      const token = localStorage.getItem("token");
      const response = await axios.patch(
        `${BASE_API}/update-status`,
        { id: screen.id, isActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnackbar(
        response.data?.message ||
          (isActive ? "Screen activated successfully" : "Screen deactivated successfully"),
        "success"
      );
    } catch (error) {
      setScreens((prev) =>
        prev.map((s) => (s.id === screen.id ? { ...s, isActive: !isActive } : s))
      );
      showSnackbar(
        error.response?.data?.message || "Failed to update screen status",
        "error"
      );
    } finally {
      setTogglingScreenId(null);
    }
  };

  const deleteScreen = async (id) => {
    const screen = screens.find((s) => s.id === id);
    if (!screen) return;

    // New screens not yet saved – just remove from state
    if (screen.isNew) {
      setScreens(screens.filter((s) => s.id !== id));
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      await axios.post(`${BASE_API}/delete/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setScreens(screens.filter((s) => s.id !== id));
      showSnackbar("Screen deleted successfully", "success");
    } catch (error) {
      console.error("Delete screen error:", error.response?.data || error.message);
      showSnackbar(
        error.response?.data?.message || "Failed to delete screen",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;

    const newScreens = [...screens];
    const draggedScreen = newScreens[draggedItem];
    newScreens.splice(draggedItem, 1);
    newScreens.splice(index, 0, draggedScreen);

    setScreens(newScreens.map((s, i) => ({ ...s, order: i })));
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const saveScreen = async (screen) => {
    if (!screen.files || screen.files.length === 0) {
      showSnackbar("Please upload at least one file", "warning");
      return false;
    }

    const formData = new FormData();

    // Append files
    screen.files.forEach((file) => {
      formData.append("files", file);
    });

    // Append fields
    formData.append("title", screen.title || "");
    formData.append("description", screen.description || "");
    formData.append("type", screen.type);
    formData.append("order", String(screen.order));
    formData.append("isActive", String(screen.isActive));

    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${BASE_API}/create-screens`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("API RESPONSE:", response.data);

      showSnackbar("Screen saved successfully", "success");
      return true;
    } catch (error) {
      console.error(
        "SAVE SCREEN ERROR:",
        error.response?.data || error.message
      );

      showSnackbar(
        error.response?.data?.message || "Failed to save screen",
        "error"
      );
      return false;
    }
  };

  const saveAllScreens = async () => {
    setLoading(true);
    let successCount = 0;
    let failCount = 0;

    // Filter only new screens that have files
    const screensToSave = screens.filter(
      (screen) =>
        (screen.isNew || screen.files?.length > 0) && screen.files?.length > 0
    );

    if (screensToSave.length === 0) {
      showSnackbar(
        "No new screens to save. Please add files to your screens.",
        "warning"
      );
      setLoading(false);
      return;
    }
    for (const screen of screensToSave) {
      const success = await saveScreen(screen);
      if (success) {
        successCount++;
      } else {
        failCount++;
      }
    }

    setLoading(false);

    if (successCount > 0) {
      showSnackbar(`Successfully saved ${successCount} screen(s)!`, "success");
      await fetchScreens(); // Refresh the list
    }

    if (failCount > 0) {
      showSnackbar(
        `Failed to save ${failCount} screen(s). Check console for details.`,
        "error"
      );
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
    setTimeout(
      () => setSnackbar({ open: false, message: "", severity: "success" }),
      3000
    );
  };

  const getMediaTypeIcon = (type) => {
    switch (type) {
      case "image":
        return <ImageIcon />;
      case "video":
        return <VideoLibrary />;
      case "gif":
        return <Gif />;
      default:
        return <ImageIcon />;
    }
  };

  const MobilePreview = () => {
    const currentScreen = screens[currentPreviewIndex];

    useEffect(() => {
      if (!previewMode || screens.length === 0) return;

      const timer = setTimeout(() => {
        setCurrentPreviewIndex((prev) =>
          prev === screens.length - 1 ? 0 : prev + 1
        );
      }, 3000); // 3 seconds per screen

      return () => clearTimeout(timer);
    }, [currentPreviewIndex, previewMode, screens.length]);

    if (!currentScreen) return null;

    const getPreviewUrl = () => {
      // 1️⃣ New screen with local file preview
      if (currentScreen.filePreviews?.length > 0) {
        return currentScreen.filePreviews[0];
      }

      // 2️⃣ Existing screen from backend - url is already a complete URL
      if (currentScreen.url) {
        return currentScreen.url;
      }

      // 3️⃣ Fallback: check for media array (if API structure changes)
      if (currentScreen.media && currentScreen.media.length > 0) {
        const mediaUrl = currentScreen.media[0].url;
        // Check if URL is already complete
        if (mediaUrl.startsWith("http")) {
          return mediaUrl;
        }
        return `${process.env.REACT_APP_API_BASE_URL}/${mediaUrl}`;
      }

      return null;
    };

    const previewUrl = getPreviewUrl();

    return (
      <DialogContent
        sx={{
          position: "relative",
          p: 0,
          minHeight: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ position: "relative" }}>
          <Box
            sx={{
              width: 375,
              height: 667,
              bgcolor: "white",
              borderRadius: "24px",
              overflow: "hidden",
              border: "8px solid #1f1f1f",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            <Box
              sx={{
                height: 24,
                bgcolor: "#1f1f1f",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 4,
                  bgcolor: "#555",
                  borderRadius: 2,
                }}
              />
            </Box>
            <Box
              sx={{
                height: 635,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
                background: "linear-gradient(to bottom, #e3f2fd, #ffffff)",
              }}
            >
              {previewUrl && (
                <>
                  {currentScreen.type === "video" ? (
                    <video
                      src={previewUrl}
                      autoPlay
                      loop
                      muted
                      playsInline
                      controls
                      style={{
                        width: "100%",
                        maxHeight: 360,
                        objectFit: "contain",
                        marginBottom: 24,
                      }}
                    />
                  ) : (
                    <img
                      src={previewUrl}
                      alt={currentScreen.title}
                      style={{
                        width: "100%",
                        maxHeight: 360,
                        objectFit: "contain",
                        marginBottom: 24,
                      }}
                    />
                  )}
                </>
              )}
              {currentScreen.title && (
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  textAlign="center"
                  mb={1.5}
                  color="text.primary"
                >
                  {currentScreen.title}
                </Typography>
              )}
              {currentScreen.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  mb={4}
                >
                  {currentScreen.description}
                </Typography>
              )}
              <Stack direction="row" spacing={1} mb={2}>
                {screens.map((_, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor:
                        idx === currentPreviewIndex ? "#1976d2" : "#bdbdbd",
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        height: "100vh",
        fontFamily: "Arial, sans-serif",
        paddingTop: "70px",
      }}
    >
      <div
        style={{
          flex: 16,
          overflowY: "auto",
          padding: 16,
          boxSizing: "border-box",

          borderRight: "1px solid #ddd",
          backgroundColor: "#f9f9f9",
        }}
      >
        <Box sx={{ p: 3, pt: "100px" }}>
          {snackbar.open && (
            <Alert severity={snackbar.severity} sx={{ mb: 2 }}>
              {snackbar.message}
            </Alert>
          )}

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Show Intro Screen
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Control how intro screens are shown on the mobile app
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showIntroScreen}
                        onChange={(e) => setShowIntroScreen(e.target.checked)}
                        disabled={!canCreate && !canEdit}
                      />
                    }
                    label={showIntroScreen ? "Enabled" : "Disabled"}
                  />
                  {(canCreate || canEdit) && (
                    <Button
                      variant="outlined"
                      onClick={saveShowIntroScreenSetting}
                      disabled={loading}
                    >
                      Save
                    </Button>
                  )}
                </Box>
              </Box>
              <Alert severity="info">
                {showIntroScreen
                  ? "When enabled, intro screens will appear every time the user opens the app."
                  : "When disabled, intro screens will be shown only the first time. After that, they will not appear again."}
              </Alert>
            </CardContent>
          </Card>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h4" fontWeight="bold">
              Intro Screen Manager
            </Typography>
            <Stack direction="row" spacing={2}>
              {canCreate && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={addNewScreen}
                  >
                    Add Screen
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={saveAllScreens}
                    disabled={loading || screens.length === 0}
                    sx={{
                      background:
                        "linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)",
                      "&:hover": {
                        background:
                          "linear-gradient(90deg, #5f22bc 0%, #8b36c2 76.93%)",
                      },
                    }}
                  >
                    {loading ? "Saving..." : "Save All"}
                  </Button>
                </>
              )}
            </Stack>
          </Box>

          <Grid container spacing={3}>
            {screens.map((screen, index) => (
              <Grid item xs={12} md={6} key={screen.id}>
                <Card
                  draggable={canEdit}
                  onDragStart={canEdit ? (e) => handleDragStart(e, index) : undefined}
                  onDragOver={canEdit ? (e) => handleDragOver(e, index) : undefined}
                  onDragEnd={canEdit ? handleDragEnd : undefined}
                  sx={{
                    opacity: draggedItem === index ? 0.5 : 1,
                    cursor: canEdit ? "move" : "default",
                    transition: "all 0.2s",
                    "&:hover": { boxShadow: 4 },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {canEdit && (
                          <DragIndicator
                            sx={{ color: "grey.400", cursor: "grab" }}
                          />
                        )}
                        <Chip label={`Screen ${index + 1}`} size="small" />
                      </Box>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <Chip
                          label={screen.isActive ? "Active" : "Inactive"}
                          size="small"
                          color={screen.isActive ? "success" : "default"}
                          variant={screen.isActive ? "filled" : "outlined"}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(screen.isActive)}
                              onChange={(e) =>
                                updateScreenStatus(screen, e.target.checked)
                              }
                              size="small"
                              disabled={!canEdit || togglingScreenId === screen.id}
                            />
                          }
                          label="Active"
                        />
                        {canDelete && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => deleteScreen(screen.id)}
                        >
                          <Delete />
                        </IconButton>
                        )}
                      </Box>
                    </Box>

                    <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={!canEdit}>
                      <InputLabel>Media Type</InputLabel>
                      <Select
                        value={screen.type}
                        label="Media Type"
                        onChange={(e) =>
                          updateScreen(screen.id, "type", e.target.value)
                        }
                      >
                        <MenuItem value="image">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <ImageIcon fontSize="small" />
                            Image
                          </Box>
                        </MenuItem>
                        <MenuItem value="video">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <VideoLibrary fontSize="small" />
                            Video
                          </Box>
                        </MenuItem>
                        <MenuItem value="gif">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Gif fontSize="small" />
                            GIF
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>

                    <Box
                      sx={{
                        border: "2px dashed",
                        borderColor: "grey.300",
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        textAlign: "center",
                        bgcolor: "grey.50",
                      }}
                    >
                      {screen.files && screen.files.length > 0 ? (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={1}
                          >
                            {screen.files.length} file(s) selected
                          </Typography>
                          <Grid container spacing={1} sx={{ mb: 2 }}>
                            {screen.filePreviews?.map((preview, idx) => (
                              <Grid item xs={6} key={idx}>
                                <Box sx={{ position: "relative" }}>
                                  {screen.type === "video" ? (
                                    <video
                                      src={preview}
                                      style={{
                                        width: "100%",
                                        height: 100,
                                        objectFit: "cover",
                                        borderRadius: 4,
                                      }}
                                    />
                                  ) : (
                                    <img
                                      src={preview}
                                      alt={`Preview ${idx}`}
                                      style={{
                                        width: "100%",
                                        height: 100,
                                        objectFit: "cover",
                                        borderRadius: 4,
                                      }}
                                    />
                                  )}
                                  {canEdit && (
                                  <IconButton
                                    size="small"
                                    sx={{
                                      position: "absolute",
                                      top: 2,
                                      right: 2,
                                      bgcolor: "rgba(255,255,255,0.9)",
                                      "&:hover": { bgcolor: "white" },
                                    }}
                                    onClick={() => removeFile(screen.id, idx)}
                                  >
                                    <Close fontSize="small" />
                                  </IconButton>
                                  )}
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      ) : screen.url ? (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={1}
                          >
                            Uploaded file
                          </Typography>
                          <Box>
                            {screen.type === "video" ? (
                              <video
                                src={screen.url}
                                style={{
                                  width: "100%",
                                  maxHeight: 200,
                                  objectFit: "cover",
                                  borderRadius: 4,
                                }}
                              />
                            ) : (
                              <img
                                src={screen.url}
                                alt="Uploaded media"
                                style={{
                                  width: "100%",
                                  maxHeight: 200,
                                  objectFit: "cover",
                                  borderRadius: 4,
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      ) : screen.media && screen.media.length > 0 ? (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={1}
                          >
                            {screen.media.length} file(s) uploaded
                          </Typography>
                          <Grid container spacing={1}>
                            {screen.media.slice(0, 4).map((media, idx) => (
                              <Grid item xs={6} key={idx}>
                                {media.mediaType?.startsWith("video/") ? (
                                  <video
                                    src={`${process.env.REACT_APP_API_BASE_URL}/${media.url}`}
                                    style={{
                                      width: "100%",
                                      height: 100,
                                      objectFit: "cover",
                                      borderRadius: 4,
                                    }}
                                  />
                                ) : (
                                  <img
                                    src={`${process.env.REACT_APP_API_BASE_URL}/${media.url}`}
                                    alt={`Media ${idx}`}
                                    style={{
                                      width: "100%",
                                      height: 100,
                                      objectFit: "cover",
                                      borderRadius: 4,
                                    }}
                                  />
                                )}
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      ) : (
                        <Box>
                          <Upload
                            sx={{ fontSize: 48, color: "grey.400", mb: 1 }}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={1}
                          >
                            Upload{" "}
                            {screen.type === "image"
                              ? "Images"
                              : screen.type === "video"
                              ? "Videos"
                              : "GIFs"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Multiple files supported
                          </Typography>
                        </Box>
                      )}
                      {(canCreate || canEdit) && (
                      <Button
                        variant="outlined"
                        component="label"
                        size="small"
                        sx={{ mt: 1 }}
                      >
                        {screen.files?.length > 0 ||
                        screen.url ||
                        screen.media?.length > 0
                          ? "Change Files"
                          : "Choose Files"}
                        <input
                          type="file"
                          hidden
                          multiple
                          accept={
                            screen.type === "image"
                              ? "image/*"
                              : screen.type === "video"
                              ? "video/*"
                              : "image/gif"
                          }
                          onChange={(e) =>
                            handleFileSelect(screen.id, e.target.files)
                          }
                        />
                      </Button>
                      )}
                    </Box>

                    <TextField
                      fullWidth
                      label="Title (Optional)"
                      placeholder="Enter screen title"
                      value={screen.title}
                      onChange={(e) =>
                        updateScreen(screen.id, "title", e.target.value)
                      }
                      size="small"
                      sx={{ mb: 2 }}
                      disabled={!canEdit}
                    />

                    <TextField
                      fullWidth
                      label="Description (Optional)"
                      placeholder="Enter screen description"
                      value={screen.description}
                      onChange={(e) =>
                        updateScreen(screen.id, "description", e.target.value)
                      }
                      multiline
                      rows={3}
                      size="small"
                      disabled={!canEdit}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {screens.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" color="text.secondary" mb={3}>
                No intro screens added yet
              </Typography>
              {canCreate && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={addNewScreen}
                  sx={{
                    background:
                      "linear-gradient(90deg, #7028DA 0%, #9E3ED7 76.93%)",
                    "&:hover": {
                      background:
                        "linear-gradient(90deg, #5f22bc 0%, #8b36c2 76.93%)",
                    },
                  }}
                >
                  Add Your First Screen
                </Button>
              )}
            </Box>
          )}
        </Box>
      </div>
      <div></div>
      {<MobilePreview />}
    </div>
  );
};

export default IntroScreenManager;
